import { useMemo, useState, type ChangeEvent } from 'react';

const MAX_CSV_BYTES = 1_000_000;
const MAX_CSV_ROWS = 200;

const operationKinds = [
  'private',
  'corporate',
  'shared',
  'commercial',
  'ferry',
  'training',
  'maintenance',
  'support',
  'other',
] as const;

const localizedKinds: Readonly<Record<string, (typeof operationKinds)[number]>> = {
  privada: 'private',
  private: 'private',
  corporativa: 'corporate',
  corporate: 'corporate',
  compartilhada: 'shared',
  shared: 'shared',
  comercial: 'commercial',
  commercial: 'commercial',
  traslado: 'ferry',
  ferry: 'ferry',
  treinamento: 'training',
  training: 'training',
  manutencao: 'maintenance',
  maintenance: 'maintenance',
  apoio: 'support',
  support: 'support',
  outra: 'other',
  outro: 'other',
  other: 'other',
};

const columnAliases = {
  externalKey: ['chave_externa', 'external_key', 'id_externo'],
  missionName: ['missao', 'missão', 'mission_name', 'mission'],
  kind: ['tipo', 'tipo_operacao', 'tipo_operação', 'kind'],
  originCode: ['origem_codigo', 'origem_código', 'origin_code'],
  originName: ['origem_nome', 'origin_name', 'origem'],
  destinationCode: ['destino_codigo', 'destino_código', 'destination_code'],
  destinationName: ['destino_nome', 'destination_name', 'destino'],
  plannedDeparture: ['partida_planejada', 'planned_departure', 'partida'],
  plannedArrival: ['chegada_planejada', 'planned_arrival', 'chegada'],
  base: ['base_codigo', 'base_código', 'base'],
  aircraftRegistration: ['matricula', 'matrícula', 'aircraft_registration', 'aeronave'],
  coordinatorEmail: ['coordenador_email', 'coordinator_email', 'email_coordenador'],
} as const;

export interface OperationCsvReferenceData {
  readonly bases: readonly { id: string; name: string; airportCode: string | null }[];
  readonly aircraft: readonly {
    id: string;
    registrationMark: string;
    manufacturer: string;
    model: string;
  }[];
  readonly coordinators: readonly { userId: string; name: string; email: string }[];
}

interface OperationImportPayload {
  readonly operationId: string;
  readonly idempotencyKey: string;
  readonly kind: (typeof operationKinds)[number];
  readonly missionName: string;
  readonly baseId: string | null;
  readonly aircraftId: string | null;
  readonly assignedCoordinatorUserId: string | null;
  readonly legs: readonly {
    originCode: string | null;
    originName: string;
    destinationCode: string | null;
    destinationName: string;
    plannedDeparture: string;
    plannedArrival: string;
  }[];
}

export type OperationCsvRowStatus =
  'ready' | 'invalid' | 'importing' | 'created' | 'replayed' | 'failed';

export interface OperationCsvPreviewRow {
  readonly rowNumber: number;
  readonly externalKey: string | null;
  readonly missionName: string;
  readonly route: string;
  readonly plannedDeparture: string | null;
  readonly issues: readonly string[];
  readonly payload?: OperationImportPayload;
  readonly status: OperationCsvRowStatus;
  readonly result?: string;
}

export class OperationCsvStructureError extends Error {}

function normalizeHeader(value: string): string {
  return value
    .replace(/^\uFEFF/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function parseDelimitedRecords(text: string, delimiter: ',' | ';'): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (!quoted && character === delimiter) {
      record.push(field);
      field = '';
      continue;
    }
    if (!quoted && (character === '\n' || character === '\r')) {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      record.push(field);
      if (record.some((value) => value.trim().length > 0)) records.push(record);
      record = [];
      field = '';
      continue;
    }
    field += character;
  }

  if (quoted) throw new OperationCsvStructureError('Há uma coluna com aspas não finalizadas.');
  record.push(field);
  if (record.some((value) => value.trim().length > 0)) records.push(record);
  return records;
}

function countDelimiterOnHeader(text: string, delimiter: ',' | ';'): number {
  let count = 0;
  let quoted = false;
  for (const character of text) {
    if (character === '"') quoted = !quoted;
    else if (!quoted && character === delimiter) count += 1;
    else if (!quoted && (character === '\n' || character === '\r')) break;
  }
  return count;
}

function readColumn(record: Readonly<Record<string, string>>, aliases: readonly string[]): string {
  for (const alias of aliases) {
    const value = record[normalizeHeader(alias)];
    if (value !== undefined) return value.trim();
  }
  return '';
}

function hasColumn(headers: readonly string[], aliases: readonly string[]): boolean {
  const normalizedAliases = aliases.map(normalizeHeader);
  return headers.some((header) => normalizedAliases.includes(header));
}

function normalizeRegistration(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function normalizeAirportCode(value: string): string {
  return value.trim().toUpperCase();
}

function parseCsvDate(value: string): Date | null {
  const brazilian = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})$/.exec(value);
  if (brazilian) {
    const [, day, month, year, hour, minute] = brazilian;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
    );
    return date.getFullYear() === Number(year) &&
      date.getMonth() === Number(month) - 1 &&
      date.getDate() === Number(day) &&
      date.getHours() === Number(hour) &&
      date.getMinutes() === Number(minute)
      ? date
      : null;
  }

  const iso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?$/.test(
    value,
  );
  if (!iso) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function uuidFromBytes(bytes: Uint8Array): string {
  const uuidBytes = bytes.slice(0, 16);
  uuidBytes[6] = ((uuidBytes[6] ?? 0) & 0x0f) | 0x50;
  uuidBytes[8] = ((uuidBytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...uuidBytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function deterministicImportUuid(seed: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed));
  return uuidFromBytes(new Uint8Array(digest));
}

export async function parseOperationCsv(
  text: string,
  organizationId: string,
  referenceData: OperationCsvReferenceData,
): Promise<OperationCsvPreviewRow[]> {
  const delimiter =
    countDelimiterOnHeader(text, ';') >= countDelimiterOnHeader(text, ',') ? ';' : ',';
  const records = parseDelimitedRecords(text, delimiter);
  const headerRecord = records[0];
  if (!headerRecord) throw new OperationCsvStructureError('O arquivo está vazio.');

  const headers = headerRecord.map(normalizeHeader);
  if (headers.some((header) => header.length === 0)) {
    throw new OperationCsvStructureError('O cabeçalho contém uma coluna sem nome.');
  }
  if (new Set(headers).size !== headers.length) {
    throw new OperationCsvStructureError('O cabeçalho contém colunas repetidas.');
  }
  const requiredColumns = [
    ['missão', columnAliases.missionName],
    ['origem_nome', columnAliases.originName],
    ['destino_nome', columnAliases.destinationName],
    ['partida_planejada', columnAliases.plannedDeparture],
    ['chegada_planejada', columnAliases.plannedArrival],
  ] as const;
  const missing = requiredColumns
    .filter(([, aliases]) => !hasColumn(headers, aliases))
    .map(([label]) => label);
  if (missing.length > 0) {
    throw new OperationCsvStructureError(`Colunas obrigatórias ausentes: ${missing.join(', ')}.`);
  }

  const dataRecords = records.slice(1);
  if (dataRecords.length === 0) throw new OperationCsvStructureError('O arquivo não contém dados.');
  if (dataRecords.length > MAX_CSV_ROWS) {
    throw new OperationCsvStructureError(`O arquivo excede o limite de ${MAX_CSV_ROWS} linhas.`);
  }

  const seenIdentities = new Set<string>();
  const previews: OperationCsvPreviewRow[] = [];
  const basesByReference = new Map<string, OperationCsvReferenceData['bases'][number]>();
  for (const base of referenceData.bases) {
    if (base.airportCode) basesByReference.set(normalizeAirportCode(base.airportCode), base);
    basesByReference.set(base.name.trim().toLowerCase(), base);
  }
  const aircraftByRegistration = new Map(
    referenceData.aircraft.map((aircraft) => [
      normalizeRegistration(aircraft.registrationMark),
      aircraft,
    ]),
  );
  const coordinatorsByEmail = new Map(
    referenceData.coordinators.map((coordinator) => [
      coordinator.email.trim().toLowerCase(),
      coordinator,
    ]),
  );

  for (const [index, values] of dataRecords.entries()) {
    const record = Object.fromEntries(
      headers.map((header, column) => [header, values[column] ?? '']),
    );
    const issues: string[] = [];
    if (
      values.length > headers.length &&
      values.slice(headers.length).some((value) => value.trim())
    ) {
      issues.push('Há colunas extras sem cabeçalho.');
    }

    const externalKey = readColumn(record, columnAliases.externalKey) || null;
    const normalizedExternalKey = normalizeHeader(externalKey ?? '');
    const missionName = readColumn(record, columnAliases.missionName);
    const rawKind = normalizeHeader(readColumn(record, columnAliases.kind) || 'commercial');
    const kindCandidate = localizedKinds[rawKind];
    const kind =
      kindCandidate && operationKinds.includes(kindCandidate) ? kindCandidate : undefined;
    const originCode = normalizeAirportCode(readColumn(record, columnAliases.originCode));
    const originName = readColumn(record, columnAliases.originName);
    const destinationCode = normalizeAirportCode(readColumn(record, columnAliases.destinationCode));
    const destinationName = readColumn(record, columnAliases.destinationName);
    const rawDeparture = readColumn(record, columnAliases.plannedDeparture);
    const rawArrival = readColumn(record, columnAliases.plannedArrival);
    const departure = parseCsvDate(rawDeparture);
    const arrival = parseCsvDate(rawArrival);
    const rawBase = readColumn(record, columnAliases.base);
    const rawRegistration = readColumn(record, columnAliases.aircraftRegistration);
    const rawCoordinatorEmail = readColumn(record, columnAliases.coordinatorEmail).toLowerCase();

    if (missionName.length < 3 || missionName.length > 160) {
      issues.push('A missão deve ter entre 3 e 160 caracteres.');
    }
    if (externalKey && (normalizedExternalKey.length === 0 || externalKey.length > 120)) {
      issues.push('A chave externa deve conter letras ou números e ter no máximo 120 caracteres.');
    }
    if (!kind) issues.push('O tipo de operação não é reconhecido.');
    if (originCode && !/^[A-Z0-9]{3,4}$/.test(originCode)) {
      issues.push('O código de origem deve ter 3 ou 4 caracteres.');
    }
    if (originName.length < 2 || originName.length > 160) {
      issues.push('A origem deve ter entre 2 e 160 caracteres.');
    }
    if (destinationCode && !/^[A-Z0-9]{3,4}$/.test(destinationCode)) {
      issues.push('O código de destino deve ter 3 ou 4 caracteres.');
    }
    if (destinationName.length < 2 || destinationName.length > 160) {
      issues.push('O destino deve ter entre 2 e 160 caracteres.');
    }
    if (!departure) issues.push('A partida planejada é inválida.');
    if (!arrival) issues.push('A chegada planejada é inválida.');
    if (departure && arrival && arrival.getTime() <= departure.getTime()) {
      issues.push('A chegada deve ocorrer depois da partida.');
    }

    const normalizedBase = normalizeAirportCode(rawBase);
    const base = rawBase
      ? (basesByReference.get(normalizedBase) ?? basesByReference.get(rawBase.trim().toLowerCase()))
      : undefined;
    if (rawBase && !base) issues.push('A base não foi encontrada nesta organização.');

    const normalizedRegistration = normalizeRegistration(rawRegistration);
    const aircraft = rawRegistration
      ? aircraftByRegistration.get(normalizedRegistration)
      : undefined;
    if (rawRegistration && !aircraft) {
      issues.push('A matrícula não corresponde a uma aeronave ativa da organização.');
    }

    const coordinator = rawCoordinatorEmail
      ? coordinatorsByEmail.get(rawCoordinatorEmail)
      : undefined;
    if (rawCoordinatorEmail && !coordinator) {
      issues.push('O e-mail do coordenador não pertence a um membro ativo.');
    }

    const canonicalRow = JSON.stringify({
      missionName: missionName.trim(),
      kind: kind ?? rawKind,
      originCode: originCode || null,
      originName: originName.trim(),
      destinationCode: destinationCode || null,
      destinationName: destinationName.trim(),
      plannedDeparture: departure?.toISOString() ?? rawDeparture,
      plannedArrival: arrival?.toISOString() ?? rawArrival,
      baseId: base?.id ?? (rawBase || null),
      aircraftId: aircraft?.id ?? (rawRegistration || null),
      assignedCoordinatorUserId: coordinator?.userId ?? (rawCoordinatorEmail || null),
    });
    const identity = externalKey ? `external:${normalizedExternalKey}` : `content:${canonicalRow}`;
    if (seenIdentities.has(identity)) issues.push('Esta linha está duplicada no arquivo.');
    seenIdentities.add(identity);

    let payload: OperationImportPayload | undefined;
    if (issues.length === 0 && kind && departure && arrival) {
      const [operationId, idempotencyKey] = await Promise.all([
        deterministicImportUuid(`${organizationId}\u0000operation\u0000${identity}`),
        deterministicImportUuid(`${organizationId}\u0000idempotency\u0000${identity}`),
      ]);
      payload = {
        operationId,
        idempotencyKey,
        kind,
        missionName,
        baseId: base?.id ?? null,
        aircraftId: aircraft?.id ?? null,
        assignedCoordinatorUserId: coordinator?.userId ?? null,
        legs: [
          {
            originCode: originCode || null,
            originName,
            destinationCode: destinationCode || null,
            destinationName,
            plannedDeparture: departure.toISOString(),
            plannedArrival: arrival.toISOString(),
          },
        ],
      };
    }

    previews.push({
      rowNumber: index + 2,
      externalKey,
      missionName: missionName || 'Missão não informada',
      route: `${originCode || originName || 'Origem'} → ${destinationCode || destinationName || 'Destino'}`,
      plannedDeparture: departure?.toISOString() ?? null,
      issues,
      ...(payload ? { payload } : {}),
      status: payload ? 'ready' : 'invalid',
    });
  }

  return previews;
}

function statusLabel(row: OperationCsvPreviewRow): string {
  if (row.status === 'ready') return 'Pronta';
  if (row.status === 'invalid') return 'Corrigir';
  if (row.status === 'importing') return 'Importando…';
  if (row.status === 'created') return 'Criada';
  if (row.status === 'replayed') return 'Já existente';
  return 'Falhou';
}

function formatDate(value: string | null): string {
  if (!value) return 'Data inválida';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(value),
  );
}

const template = [
  'chave_externa;missao;tipo;origem_codigo;origem_nome;destino_codigo;destino_nome;partida_planejada;chegada_planejada;base_codigo;matricula;coordenador_email',
  'OP-001;Executivo Rio — São Paulo;comercial;SBJR;Jacarepaguá;SBSP;Congonhas;03/09/2026 09:00;03/09/2026 10:15;;;coordenador.teste@aeronexo.com.br',
].join('\n');

export function OperationCsvImport({
  organizationId,
  referenceData,
  onExpiredSession,
  onImported,
}: {
  readonly organizationId: string;
  readonly referenceData: OperationCsvReferenceData;
  readonly onExpiredSession: () => Promise<void>;
  readonly onImported: () => Promise<void> | void;
}) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<readonly OperationCsvPreviewRow[]>([]);
  const [structureError, setStructureError] = useState<string>();
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const summary = useMemo(
    () =>
      rows.reduce(
        (counts, row) => ({
          ...counts,
          [row.status]: counts[row.status] + 1,
        }),
        { ready: 0, invalid: 0, importing: 0, created: 0, replayed: 0, failed: 0 },
      ),
    [rows],
  );

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setRows([]);
    setStructureError(undefined);
    if (!file) {
      setFileName('');
      return;
    }
    setFileName(file.name);
    if (file.size > MAX_CSV_BYTES) {
      setStructureError('O arquivo excede o limite de 1 MB.');
      return;
    }
    setIsParsing(true);
    try {
      setRows(await parseOperationCsv(await file.text(), organizationId, referenceData));
    } catch (error) {
      setStructureError(
        error instanceof OperationCsvStructureError
          ? error.message
          : 'Não foi possível interpretar este arquivo.',
      );
    } finally {
      setIsParsing(false);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(new Blob([template], { type: 'text/csv;charset=utf-8' }));
    link.href = objectUrl;
    link.download = 'modelo-operacoes-aeronexo.csv';
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  const updateRow = (rowNumber: number, changes: Partial<OperationCsvPreviewRow>) => {
    setRows((current) =>
      current.map((row) => (row.rowNumber === rowNumber ? { ...row, ...changes } : row)),
    );
  };

  const importRows = async () => {
    if (summary.ready === 0 || summary.invalid > 0 || isImporting) return;
    setIsImporting(true);
    try {
      for (const row of rows) {
        if (row.status !== 'ready' || !row.payload) continue;
        updateRow(row.rowNumber, { status: 'importing' });
        try {
          const response = await fetch(`/v1/organizations/${organizationId}/operations`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(row.payload),
          });
          if (response.status === 401) {
            await onExpiredSession();
            return;
          }
          if (!response.ok) {
            updateRow(row.rowNumber, {
              status: 'failed',
              result:
                response.status === 409
                  ? 'Conflito: a chave externa já foi usada com outros dados.'
                  : response.status === 400
                    ? 'O servidor rejeitou os dados desta linha.'
                    : `Falha HTTP ${response.status}; o reenvio permanece seguro.`,
            });
            continue;
          }
          const result = (await response.json()) as {
            replayed: boolean;
            operation: { referenceCode: string };
          };
          updateRow(row.rowNumber, {
            status: result.replayed ? 'replayed' : 'created',
            result: result.operation.referenceCode,
          });
        } catch {
          updateRow(row.rowNumber, {
            status: 'failed',
            result: 'Falha de rede; tente novamente sem risco de duplicação.',
          });
        }
      }
      await onImported();
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <section className="csvImport" aria-labelledby="csv-import-title">
      <header className="csvImportHeader">
        <div>
          <span className="sectionEyebrow">Carga assistida</span>
          <h3 id="csv-import-title">Importar operações por CSV</h3>
          <p>O arquivo é validado no navegador antes de qualquer operação ser criada.</p>
        </div>
        <button type="button" onClick={downloadTemplate}>
          Baixar modelo
        </button>
      </header>

      <label className="csvFilePicker">
        <span>Arquivo CSV</span>
        <input
          accept=".csv,text/csv"
          disabled={isImporting}
          type="file"
          onChange={(event) => void handleFile(event)}
        />
        <small>{fileName || 'Até 200 linhas e 1 MB; use vírgula ou ponto e vírgula.'}</small>
      </label>

      {isParsing ? <div role="status">Preparando a prévia…</div> : null}
      {structureError ? (
        <div className="inlineError" role="alert">
          {structureError}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <>
          <div className="csvImportSummary" aria-label="Resumo da importação">
            <strong>{rows.length} linha(s)</strong>
            <span>{summary.ready} pronta(s)</span>
            <span>{summary.invalid} com erro</span>
            <span>{summary.created} criada(s)</span>
            <span>{summary.replayed} já existente(s)</span>
            <span>{summary.failed} falha(s)</span>
          </div>
          <div className="csvPreviewTableWrap">
            <table className="csvPreviewTable">
              <thead>
                <tr>
                  <th>Linha</th>
                  <th>Missão</th>
                  <th>Rota</th>
                  <th>Partida</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.rowNumber} className={`csvRow-${row.status}`}>
                    <td>{row.rowNumber}</td>
                    <td>
                      <strong>{row.missionName}</strong>
                      {row.externalKey ? <small>Chave: {row.externalKey}</small> : null}
                    </td>
                    <td>{row.route}</td>
                    <td>{formatDate(row.plannedDeparture)}</td>
                    <td>
                      <strong>{statusLabel(row)}</strong>
                      {row.issues.map((issue) => (
                        <small key={issue}>{issue}</small>
                      ))}
                      {row.result ? <small>{row.result}</small> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="workflowActions">
            <span>
              {summary.invalid > 0
                ? 'Corrija o arquivo e selecione-o novamente para liberar a importação.'
                : 'A mesma chave pode ser reenviada sem criar outra operação.'}
            </span>
            <button
              className="primaryButton"
              disabled={summary.ready === 0 || summary.invalid > 0 || isImporting}
              type="button"
              onClick={() => void importRows()}
            >
              {isImporting ? 'Importando…' : `Importar ${summary.ready} operação(ões)`}
            </button>
          </footer>
        </>
      ) : null}
    </section>
  );
}
